# Authentication

Every `/api/v1/*` request uses HTTP Basic Auth with your store's API key pair.

## API key shape

| Part | Example | Where it lives |
|---|---|---|
| Public key | `pk_a61285c0a552f855951f0513` | Safe to embed in client-side code (used by the SDK for tokenization) |
| Secret key | `sk_f27da89a18286d4b5dc78a7402125a6637151341c404897c` | Server-side only — shown ONCE at creation, never recoverable |

Format: `Authorization: Basic base64(public:secret)`.

```bash
# Using curl's -u flag (handles base64 for you)
curl -u "pk_a61285c0a552f855951f0513:sk_f27..." \
  https://api.n4-payments.com.br/api/v1/whoami
```

```javascript
// Node.js
const auth = Buffer.from(`${PUB}:${SEC}`).toString('base64');
fetch('https://api.n4-payments.com.br/api/v1/whoami', {
  headers: { Authorization: `Basic ${auth}` },
});
```

```python
# Python (requests handles base64 automatically)
import requests
requests.get(
    'https://api.n4-payments.com.br/api/v1/whoami',
    auth=(PUB, SEC),
)
```

## User-Agent

N4 logs a warning when `User-Agent` is missing, but accepts the request. Recommended:
`Mozilla/5.0 (compatible; your-app/1.2.3)`.

## Modes: test vs live

Every API key has a `mode`:

- `live` keys (`pk_*` / `sk_*` prefix as shown above) route only to provider accounts marked
  `environment='production'`. These hit real money.
- `test` keys route only to provider accounts marked `environment='sandbox'`. No real money moves.

A test key calling a production account → `routing_no_candidate` (and vice versa). This is a hard
filter; there is no way to accidentally cross the wire.

## Permissions

Each API key carries an explicit permission list. Examples:

| Permission | Allows |
|---|---|
| `payments.create` | `POST /api/v1/transactions` |
| `payments.read` | `GET /api/v1/transactions(/:id)` |
| `refunds.create` | `POST /api/v1/transactions/:id/refunds`, `DELETE /api/v1/transactions/:id/refund` |
| `balance.read` | `GET /api/v1/balance` |
| `customers.read` / `customers.write` | `GET` vs `POST/PATCH /api/v1/customers` |
| `recipients.read` / `recipients.write` | `GET` vs `POST/PATCH /api/v1/recipients` |
| `transfers.create` / `transfers.read` | Programmatic PIX-out endpoints |

Missing permission → `403 { error: 'forbidden' }`.

## Rotation

Compromised secret? Create a new API key, deploy, then revoke the old one. There is no "show me
the secret again" path — by design.
